import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { getSurveys, createSurvey, addSurveyQuestion, submitSurveyResponse, getSurveyResults } from '@/services/api';
import { 
  Users, Plus, BarChart3, CheckCircle2, Clock, Eye,
  Loader2, ChevronRight, ChevronLeft, Send, Award, Target
} from 'lucide-react';
import { toast } from 'sonner';

const CULTURE_CATEGORIES = [
  { value: 'reporting_culture', label: 'Reporting Culture', color: 'bg-blue-600' },
  { value: 'just_culture', label: 'Just Culture', color: 'bg-emerald-600' },
  { value: 'learning_culture', label: 'Learning Culture', color: 'bg-violet-600' },
  { value: 'informed_culture', label: 'Informed Culture', color: 'bg-amber-600' },
  { value: 'flexible_culture', label: 'Flexible Culture', color: 'bg-pink-600' }
];

const LIKERT_OPTIONS = [
  { value: '1', label: 'Strongly Disagree' },
  { value: '2', label: 'Disagree' },
  { value: '3', label: 'Neutral' },
  { value: '4', label: 'Agree' },
  { value: '5', label: 'Strongly Agree' }
];

const MATURITY_LEVELS = {
  proactive: { label: 'Proactive', color: 'bg-emerald-600', description: 'Safety is actively managed and continuously improved' },
  calculative: { label: 'Calculative', color: 'bg-blue-600', description: 'Systems are in place and performance is measured' },
  reactive: { label: 'Reactive', color: 'bg-amber-600', description: 'Response after incidents occur' },
  pathological: { label: 'Pathological', color: 'bg-red-600', description: 'Safety is not a priority' }
};

export default function SurveyPage() {
  const { t, i18n } = useTranslation();
  const { api, user } = useAuth();
  const { isRTL } = useLanguage();
  
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTakeSurvey, setShowTakeSurvey] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyResults, setSurveyResults] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Create survey form
  const [surveyForm, setSurveyForm] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    is_anonymous: true
  });
  
  // Take survey state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  
  const canManage = ['admin', 'safety_manager'].includes(user?.role);

  useEffect(() => {
    if (api) fetchSurveys();
  }, [api]);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const response = await getSurveys(api);
      setSurveys(response.data);
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSurvey = async () => {
    try {
      setSaving(true);
      await createSurvey(api, surveyForm);
      setShowCreateDialog(false);
      setSurveyForm({ title: '', description: '', start_date: '', end_date: '', is_anonymous: true });
      fetchSurveys();
      toast.success('Survey created successfully');
    } catch (error) {
      console.error('Failed to create survey:', error);
      toast.error('Failed to create survey');
    } finally {
      setSaving(false);
    }
  };

  const handleStartSurvey = (survey) => {
    setSelectedSurvey(survey);
    setCurrentQuestion(0);
    setAnswers({});
    setShowTakeSurvey(true);
  };

  const handleViewResults = async (survey) => {
    try {
      setLoading(true);
      const response = await getSurveyResults(api, survey.id);
      setSurveyResults(response.data);
      setShowResults(true);
    } catch (error) {
      console.error('Failed to fetch results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSurvey = async () => {
    try {
      setSaving(true);
      await submitSurveyResponse(api, selectedSurvey.id, answers);
      setShowTakeSurvey(false);
      toast.success('Survey submitted successfully');
    } catch (error) {
      console.error('Failed to submit survey:', error);
      toast.error('Failed to submit survey');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const activeSurveys = surveys.filter(s => s.is_active);
  const completedSurveys = surveys.filter(s => !s.is_active);

  return (
    <div className="p-6 space-y-6" data-testid="survey-page">
      {/* Header */}
      <div className={cn("flex flex-col sm:flex-row justify-between gap-4", isRTL && "sm:flex-row-reverse")}>
        <div>
          <h1 className="text-2xl font-bold text-white font-barlow flex items-center gap-2">
            <Users className="w-7 h-7 text-pink-400" />
            {t('surveys.title', 'Safety Culture Surveys')}
          </h1>
          <p className="text-slate-400 mt-1">
            {t('surveys.subtitle', 'Measure and improve organizational safety culture')}
          </p>
        </div>
        {canManage && (
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-pink-600 hover:bg-pink-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('surveys.createSurvey', 'Create Survey')}
          </Button>
        )}
      </div>

      {/* Active Surveys */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">{t('surveys.active', 'Active Surveys')}</h2>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
          </div>
        ) : activeSurveys.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {activeSurveys.map(survey => (
              <Card key={survey.id} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge className="bg-emerald-600 mb-2">Active</Badge>
                      <h3 className="text-lg font-medium text-white">{survey.title}</h3>
                      <p className="text-sm text-slate-400 mt-1">{survey.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-sm text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span>{formatDate(survey.end_date)}</span>
                    <span className="mx-2">•</span>
                    <span>{survey.questions?.length || 0} questions</span>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button 
                      size="sm"
                      className="bg-pink-600 hover:bg-pink-700 flex-1"
                      onClick={() => handleStartSurvey(survey)}
                    >
                      {t('surveys.take', 'Take Survey')}
                    </Button>
                    {canManage && (
                      <Button 
                        size="sm"
                        variant="outline"
                        className="border-slate-600"
                        onClick={() => handleViewResults(survey)}
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">{t('surveys.noActive', 'No active surveys')}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Survey Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{t('surveys.createSurvey', 'Create Survey')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('surveys.surveyTitle', 'Title')} *</Label>
              <Input
                value={surveyForm.title}
                onChange={(e) => setSurveyForm({...surveyForm, title: e.target.value})}
                placeholder="Q1 2025 Safety Culture Assessment"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <div>
              <Label>{t('common.description', 'Description')}</Label>
              <Textarea
                value={surveyForm.description}
                onChange={(e) => setSurveyForm({...surveyForm, description: e.target.value})}
                placeholder="Quarterly safety culture assessment..."
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('surveys.startDate', 'Start Date')}</Label>
                <Input
                  type="date"
                  value={surveyForm.start_date}
                  onChange={(e) => setSurveyForm({...surveyForm, start_date: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label>{t('surveys.endDate', 'End Date')}</Label>
                <Input
                  type="date"
                  value={surveyForm.end_date}
                  onChange={(e) => setSurveyForm({...surveyForm, end_date: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-600">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleCreateSurvey}
              disabled={!surveyForm.title || saving}
              className="bg-pink-600 hover:bg-pink-700"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('common.create', 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Take Survey Dialog */}
      <Dialog open={showTakeSurvey} onOpenChange={setShowTakeSurvey}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedSurvey?.title}</DialogTitle>
          </DialogHeader>
          
          {selectedSurvey?.questions?.length > 0 ? (
            <div className="space-y-6">
              {/* Progress */}
              <div>
                <div className="flex justify-between text-sm text-slate-400 mb-2">
                  <span>Question {currentQuestion + 1} of {selectedSurvey.questions.length}</span>
                  <span>{Math.round(((currentQuestion + 1) / selectedSurvey.questions.length) * 100)}%</span>
                </div>
                <Progress value={((currentQuestion + 1) / selectedSurvey.questions.length) * 100} className="h-2" />
              </div>
              
              {/* Question */}
              <div className="p-4 bg-slate-800 rounded-lg">
                <Badge className={CULTURE_CATEGORIES.find(c => c.value === selectedSurvey.questions[currentQuestion]?.category)?.color || 'bg-slate-600'}>
                  {selectedSurvey.questions[currentQuestion]?.category?.replace(/_/g, ' ')}
                </Badge>
                <p className="text-lg text-white mt-3">
                  {selectedSurvey.questions[currentQuestion]?.question}
                </p>
                
                <RadioGroup
                  value={answers[selectedSurvey.questions[currentQuestion]?.id] || ''}
                  onValueChange={(value) => setAnswers({
                    ...answers,
                    [selectedSurvey.questions[currentQuestion]?.id]: value
                  })}
                  className="mt-4 space-y-2"
                >
                  {LIKERT_OPTIONS.map(opt => (
                    <div key={opt.value} className="flex items-center space-x-2 p-2 rounded hover:bg-slate-700">
                      <RadioGroupItem value={opt.value} id={opt.value} />
                      <Label htmlFor={opt.value} className="flex-1 cursor-pointer">{opt.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              
              {/* Navigation */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  className="border-slate-600"
                  onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                  disabled={currentQuestion === 0}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                
                {currentQuestion < selectedSurvey.questions.length - 1 ? (
                  <Button
                    className="bg-pink-600 hover:bg-pink-700"
                    onClick={() => setCurrentQuestion(currentQuestion + 1)}
                    disabled={!answers[selectedSurvey.questions[currentQuestion]?.id]}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleSubmitSurvey}
                    disabled={saving || Object.keys(answers).length < selectedSurvey.questions.length}
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <Send className="w-4 h-4 mr-2" />
                    Submit
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              No questions in this survey yet.
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={showResults} onOpenChange={setShowResults}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-pink-400" />
              Safety Culture Index Results
            </DialogTitle>
          </DialogHeader>
          
          {surveyResults && (
            <div className="space-y-6">
              {/* Overall Score */}
              <div className="text-center p-6 bg-slate-800 rounded-lg">
                <div className="text-6xl font-bold text-white mb-2">
                  {surveyResults.culture_score?.overall_score || 0}
                </div>
                <Badge className={MATURITY_LEVELS[surveyResults.culture_score?.maturity_level]?.color || 'bg-slate-600'}>
                  {MATURITY_LEVELS[surveyResults.culture_score?.maturity_level]?.label || 'Unknown'}
                </Badge>
                <p className="text-sm text-slate-400 mt-2">
                  {MATURITY_LEVELS[surveyResults.culture_score?.maturity_level]?.description}
                </p>
                <p className="text-sm text-slate-500 mt-4">
                  Based on {surveyResults.response_count} responses
                </p>
              </div>
              
              {/* Dimension Scores */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-400">Culture Dimensions</h3>
                {Object.entries(surveyResults.culture_score?.dimensions || {}).map(([dim, score]) => (
                  <div key={dim} className="p-3 bg-slate-800 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white capitalize">{dim.replace(/_/g, ' ')}</span>
                      <span className={cn(
                        "font-medium",
                        score >= 80 ? "text-emerald-400" : score >= 60 ? "text-amber-400" : "text-red-400"
                      )}>
                        {score || 'N/A'}%
                      </span>
                    </div>
                    <Progress 
                      value={score || 0} 
                      className="h-2"
                    />
                  </div>
                ))}
              </div>
              
              {/* Insights */}
              {surveyResults.culture_score?.strengths?.length > 0 && (
                <div className="p-3 bg-emerald-950/30 border border-emerald-700 rounded-lg">
                  <h4 className="text-emerald-400 font-medium mb-1">Strengths</h4>
                  <p className="text-slate-300 text-sm">
                    {surveyResults.culture_score.strengths.join(', ').replace(/_/g, ' ')}
                  </p>
                </div>
              )}
              
              {surveyResults.culture_score?.areas_of_concern?.length > 0 && (
                <div className="p-3 bg-amber-950/30 border border-amber-700 rounded-lg">
                  <h4 className="text-amber-400 font-medium mb-1">Areas for Improvement</h4>
                  <p className="text-slate-300 text-sm">
                    {surveyResults.culture_score.areas_of_concern.join(', ').replace(/_/g, ' ')}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
